import formatNumber from '../utils/format-number';
import productFeatures from './product-features';
import { SUBDIRECTORY_ADDON_NAME, SUBDIRECTORY_IDENTIFIER } from '../utils/constants';

/**
 * @typedef {import('../utils/ProductUtils').Limit} Limit
 * @typedef {import('../utils/ProductUtils').Product} Product
 * @typedef {import('../utils/ProductUtils').Price} Price
 * @typedef {import('../utils/ProductUtils').Addon} Addon
 * @typedef {import('../utils/ProductUtils').Features} Features
 */

/**
 * @typedef {Product & {features: Features}} HydratedProduct
 * @typedef {Price & {features: string[]}} HydratedPrice
 */

/**
 * Process member limits and format them for display
 * @param {Limit[]} limits - Array of limit objects
 * @returns {string[]} Array of formatted member limit strings
 */
const processMemberLimits = (limits) => {
    const limitWordings = [];

    if (limits && limits.length) {
        const memberLimits = limits.find((limit) => limit.name === 'members');

        if (memberLimits && parseInt(memberLimits.value, 10) >= 1000000) {
            limitWordings.push(`${parseInt(memberLimits.value, 10) / 1000000}M members`);
        } else if (memberLimits) {
            limitWordings.push(`${formatNumber(memberLimits.value)} members`);
        }
    }

    return limitWordings;
};

/**
 * Process addon objects, adding descriptions for specific types
 * @param {Addon} addon - Addon object to process
 * @returns {Addon} Processed addon with potential modifications
 */
const processAddon = (addon) => {
    if (addon.name.toLowerCase().indexOf(SUBDIRECTORY_IDENTIFIER) >= 0) {
        return {
            ...addon,
            name: SUBDIRECTORY_ADDON_NAME,
            description:
                'If you have an existing site — for example a shop, or a static site hosted elsewhere — Ghost can be run from a separate location e.g. <code>yourdomain.com/blog</code> otherwise known as a subdirectory. <a href="https://ghost.org/help/can-i-run-ghost-from-a-subdirectory/?ref=billing.ghost.org" target="_blank" rel="noopener noreferrer">Learn more</a>.',
        };
    }

    return addon;
};

/**
 * Wrapper function that detects whether input is a product or price and calls appropriate hydrator
 * @template {Product|Price} T
 * @param {T} input - Product or Price object to hydrate
 * @returns {T extends Product ? HydratedProduct : HydratedPrice} Hydrated product or price with features
 */
function hydrateProductOrCurrentPrice(input) {
    if (!input) {
        return {};
    }

    // Products have type: 'base'
    if (input.type === 'base') {
        return hydrateProduct(input);
    }

    // Prices have base_product property
    if (input.base_product) {
        return hydrateCurrentPrice(input);
    }

    // If it's neither a product nor a price, return unchanged
    return input;
}

/**
 * Hydrate a product with features based on its name and type
 * Processes member limits, adds feature lists, and transforms addons
 * @param {Product} product - Product object to hydrate
 * @returns {HydratedProduct} Hydrated product with features and processed data
 */
function hydrateProduct(product) {
    if (!product) {
        return {};
    }

    const hydratedProduct = Object.assign({ features: [] }, product);

    if (!hydratedProduct.name) {
        return hydratedProduct;
    }

    if (!productFeatures[hydratedProduct.name]) {
        // If no features are defined for this product, return as is
        return hydratedProduct;
    }

    // 1. Process product features
    // Get features based on product type (current or legacy)
    const featuresForProduct = productFeatures[hydratedProduct.name];
    if (featuresForProduct.current) {
        // Use current features if available
        hydratedProduct.features = [...featuresForProduct.current];
    } else if (featuresForProduct.legacy) {
        // Fall back to legacy features for legacy-only products
        hydratedProduct.features = [...featuresForProduct.legacy];
    }

    // 2. Process member limits in prices
    if (hydratedProduct?.prices?.length) {
        hydratedProduct.prices.forEach((price) => {
            price.features = processMemberLimits(price.limits);
        });
    }

    // 3. Process product addons
    if (hydratedProduct?.addons?.length) {
        hydratedProduct.addons = hydratedProduct.addons.map((addon) => processAddon(addon));
    }

    return hydratedProduct;
}

/**
 * Hydrate a current price with features
 * @param {Price} currentPrice - Price object to hydrate
 * @returns {HydratedPrice} Hydrated price with features
 */
function hydrateCurrentPrice(currentPrice) {
    if (!currentPrice) {
        return {};
    }

    const hydratedPrice = Object.assign({ features: [] }, currentPrice);

    // Add product features based on whether it's legacy or current
    const featuresForProduct = productFeatures[hydratedPrice.base_product];
    if (featuresForProduct) {
        if (hydratedPrice.legacy && featuresForProduct.legacy) {
            // For legacy prices, use legacy features
            hydratedPrice.features = hydratedPrice.features.concat(featuresForProduct.legacy);
        } else if (!hydratedPrice.legacy && featuresForProduct.current) {
            // For current prices, use current features
            hydratedPrice.features = hydratedPrice.features.concat(featuresForProduct.current);
        }
    }

    // Process member limits for the price and put them last
    hydratedPrice.features.push(...processMemberLimits(hydratedPrice.limits));

    return hydratedPrice;
}

// Default export is the wrapper function
export default hydrateProductOrCurrentPrice;

// Named exports for specific functions and helpers
// mainly for test purposes
export { processMemberLimits, processAddon, hydrateProduct, hydrateCurrentPrice };
